// web/src/routes/board/Weekly.jsx
// Rider Weekly schedule — the rider-centric grid (riders × days), the spreadsheet
// "Weekly Rider Schedule" reborn. Rows come from the barn roster (/people),
// cells from /lessons in the visible week. Tap a cell to add/edit a lesson.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import LessonGrid from '../../components/lessons/LessonGrid';
import LessonEditor from '../../components/lessons/LessonEditor';
import { fmtDate } from '../../lib/dates';

const VIEW_ROLES = ['owner', 'manager', 'trainer', 'groom'];
const EDIT_ROLES = ['owner', 'manager', 'trainer'];

/**
 * Standalone at /board/week, and embedded as the Schedule board's "Riders" view.
 * Embedded, the Schedule shell owns the heading and the week nav — so this
 * renders the grid alone and takes its week from props instead of local state.
 *
 * @param embedded      drop the header/chrome; the shell supplies it
 * @param weekFrom      controlled week start (ISO Monday); omit to self-manage
 * @param onWeekChange  called with the new week start when controlled
 * @param onShowRider   called with a rider when their name is clicked; omit to
 *                     leave names as plain text
 */
export default function WeeklyBoard({
  embedded = false,
  weekFrom: weekFromProp,
  onWeekChange,
  onShowRider,
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const canView = VIEW_ROLES.includes(activeRole);
  const canEdit = EDIT_ROLES.includes(activeRole);
  const canManage = activeRole === 'owner' || activeRole === 'manager';

  const controlled = weekFromProp !== undefined;
  // null = let the server resolve the current week
  const [ownFrom, setOwnFrom] = useState(null);
  const from = controlled ? weekFromProp : ownFrom;
  const setFrom = useCallback(
    (d) => (controlled ? onWeekChange?.(d) : setOwnFrom(d)),
    [controlled, onWeekChange],
  );
  const to = from ? addDaysISO(from, 6) : null;

  const lessonsQ = useQuery({
    queryKey: ['lessons', from ?? 'current'],
    queryFn: () => api.get(from ? `/lessons?from=${from}&to=${to}` : '/lessons'),
    enabled: canView,
    retry: false,
  });
  const peopleQ = useQuery({
    queryKey: ['people'],
    queryFn: () => api.get('/people'),
    enabled: canView,
  });
  const horsesQ = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
    enabled: canView && canEdit,
  });
  // Owners power the "add an existing person" suggestions. Owner/manager-gated
  // endpoint, so only fetch when allowed; trainers just get the New-rider button.
  const ownersQ = useQuery({
    queryKey: ['owners'],
    queryFn: () => api.get('/owners'),
    enabled: canView && canManage,
  });

  // Lock onto the server's resolved week start so stepping is pure ISO math.
  useEffect(() => {
    if (from === null && lessonsQ.data?.from) setFrom(lessonsQ.data.from);
  }, [from, lessonsQ.data, setFrom]);

  const [editor, setEditor] = useState(null); // { lesson, date, riderPersonId, riderName }

  const linkRider = useMutation({
    mutationFn: (userId) => api.post('/people', { userId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['owners'] });
    },
    onError: () => alert('Couldn’t add that owner to the schedule. They may already be a rider.'),
  });
  const removeRider = useMutation({
    mutationFn: (id) => api.del(`/people/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">The weekly schedule is for barn staff.</div>
      </div>
    );
  }

  const weekFrom = from ?? lessonsQ.data?.from ?? null;
  const today = lessonsQ.data?.today ?? null;
  const days = weekFrom ? Array.from({ length: 7 }, (_, i) => addDaysISO(weekFrom, i)) : [];
  const people = peopleQ.data?.people ?? [];
  const lessons = lessonsQ.data?.lessons ?? [];

  const byRiderDate = {};
  for (const l of lessons) {
    (byRiderDate[l.riderPersonId] ??= {});
    (byRiderDate[l.riderPersonId][l.date] ??= []).push(l);
  }

  // Owners who aren't already on the roster — candidates to pull in directly.
  const riderUserIds = new Set(people.map((p) => p.userId).filter(Boolean));
  const ownerCandidates = (ownersQ.data?.owners ?? []).filter((o) => !riderUserIds.has(o.userId));

  const loading = lessonsQ.isLoading || peopleQ.isLoading || !weekFrom;

  return (
    <div className={embedded ? '' : 'max-w-7xl mx-auto p-4'}>
      {!embedded && (
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Riders</h1>
            <Link
              to="/schedule"
              className="inline-flex items-center gap-1 rounded-md border border-ink-100 px-2.5 py-1 text-sm text-ink-600 hover:text-ink-900 hover:bg-ink-50"
            >
              Full schedule <span aria-hidden>→</span>
            </Link>
          </div>
          {weekFrom && (
            <WeekStepper
              days={days}
              today={today}
              onPrev={() => setFrom(addDaysISO(weekFrom, -7))}
              onNext={() => setFrom(addDaysISO(weekFrom, 7))}
              onThisWeek={() => setFrom(null)}
            />
          )}
        </header>
      )}

      {loading ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : people.length === 0 ? (
        <div className="card p-8 text-center text-ink-500">
          No riders yet. Add your first one below to start the schedule.
        </div>
      ) : (
        <LessonGrid
          days={days}
          today={today}
          people={people}
          byRiderDate={byRiderDate}
          canEdit={canEdit}
          onRiderName={onShowRider}
          onCell={(riderPersonId, riderName, date, lesson) =>
            canEdit && setEditor({ lesson: lesson ?? null, date, riderPersonId, riderName })
          }
          onRemoveRider={
            canEdit
              ? (p) => {
                  if (confirm(`Remove ${p.name} from the schedule? Their past lessons are kept.`)) {
                    removeRider.mutate(p.id);
                  }
                }
              : null
          }
        />
      )}

      {canEdit && (
        <RiderCombo
          candidates={ownerCandidates}
          linking={linkRider.isPending}
          onLink={(userId) => linkRider.mutate(userId)}
          onAddNew={(q) => navigate(`/riders?new=${encodeURIComponent(q)}`)}
        />
      )}

      {editor && (
        <LessonEditor
          lesson={editor.lesson}
          date={editor.date}
          riderPersonId={editor.riderPersonId}
          riderName={editor.riderName}
          horses={horsesQ.data?.horses ?? []}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

// Combobox: type a name to pull in an existing owner (linked instantly), or hit
// "Add rider" to open the full New-rider form prefilled with what you typed.
function RiderCombo({ candidates, linking, onLink, onAddNew }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const needle = q.trim().toLowerCase();
  const matches = needle
    ? candidates
        .filter((c) => (c.name || c.email || '').toLowerCase().includes(needle))
        .slice(0, 6)
    : [];

  return (
    <div className="mt-4 relative max-w-md">
      <div className="flex items-center gap-2">
        <input
          className="input flex-1"
          placeholder="Add a rider…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={linking}
          onClick={() => onAddNew(q.trim())}
        >
          {linking ? 'Adding…' : 'Add Rider'}
        </button>
      </div>

      {open && matches.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-lg border border-ink-100 bg-white shadow-lg overflow-hidden">
          {matches.map((c) => (
            <li key={c.userId}>
              {/* onMouseDown fires before the input blur closes the list */}
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center justify-between gap-2"
                onMouseDown={() => { setQ(''); setOpen(false); onLink(c.userId); }}
              >
                <span className="text-sm text-ink-800 truncate">{c.name || c.email}</span>
                <span className="text-xs text-ink-400 shrink-0">Owner · add to schedule</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && needle && matches.length === 0 && candidates.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-ink-100 bg-white shadow-lg px-3 py-2 text-xs text-ink-400">
          No matching owner — “Add rider” to create a new one.
        </div>
      )}
    </div>
  );
}

function WeekStepper({ days, today, onPrev, onNext, onThisWeek }) {
  const label = days.length === 7 ? `${formatDay(days[0])} – ${formatDay(days[6])}` : '';
  const isThisWeek = today && days.includes(today);
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onPrev}
        className="w-9 h-9 rounded-md border border-ink-100 text-ink-600 text-lg active:scale-95"
        aria-label="Previous week"
      >
        ‹
      </button>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium tabular-nums">{label}</span>
        {isThisWeek ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">This week</span>
        ) : (
          <button onClick={onThisWeek} className="text-xs text-ink-500 underline">
            This week
          </button>
        )}
      </div>
      <button
        onClick={onNext}
        className="w-9 h-9 rounded-md border border-ink-100 text-ink-600 text-lg active:scale-95"
        aria-label="Next week"
      >
        ›
      </button>
    </div>
  );
}

function formatDay(iso) {
  if (!iso) return '';
  return fmtDate(iso, 'short');
}

function addDaysISO(iso, n) {
  const base = iso ?? new Date().toISOString().slice(0, 10);
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
